import {
  CapabilityDTO,
  CapabilityInput,
  OrgDTO,
  ProductInput,
  SloDTO,
  SloGroupInput,
  SloInput,
  SloType,
  TeamInput,
  TeamInputAtlassianOrgStatusEnum,
  PollinatorDTO,
  PollinatorArchetypeDTO,
} from '@atlassiansox/phobos-client-ts';
import omit from 'lodash/omit';

import { SLOCrudPayload } from '@/ui';
import { assertUnreachable, identity } from '@/utils';
import { Api2UiMappingType } from '@/meta';

export const SloTypeCompactFields: Record<API.SLOTypeName, FieldOption<API.SLOTypeName>> = {
  RELIABILITY: {
    label: 'Reliability (SFX)',
    value: SloType.Reliability,
  },

  PERFORMANCE: {
    label: 'Performance (SFX)',
    value: SloType.Performance,
  },

  UI_PERFORMANCE: {
    label: 'Performance (Glance)',
    value: SloType.UiPerformance,
  },

  RELIABILITY_CLOUDWATCH: {
    label: 'Reliability (CW)',
    value: SloType.ReliabilityCloudwatch,
  },

  PERFORMANCE_CLOUDWATCH: {
    label: 'Performance (CW)',
    value: SloType.PerformanceCloudwatch,
  },
};

export const SloTypeFields: Record<API.SLOTypeName, FieldOption<API.SLOTypeName>> = {
  RELIABILITY: {
    label: 'Reliability (SFX Query)',
    value: SloType.Reliability,
  },

  PERFORMANCE: {
    label: 'Performance (SFX Query)',
    value: SloType.Performance,
  },

  UI_PERFORMANCE: {
    label: 'Performance (Glance)',
    value: 'UI_PERFORMANCE',
  },

  RELIABILITY_CLOUDWATCH: {
    label: 'Reliability (CloudWatch)',
    value: 'RELIABILITY_CLOUDWATCH',
  },

  PERFORMANCE_CLOUDWATCH: {
    label: 'Performance (CloudWatch)',
    value: 'PERFORMANCE_CLOUDWATCH',
  },
};

export const SloTypeFieldsWithoutCW = omit(SloTypeFields, [
  'RELIABILITY_CLOUDWATCH',
  'PERFORMANCE_CLOUDWATCH',
]);

export type TeamFormData = TeamInput & {
  org: { orgId: string };
};
export type ProductFormData = ProductInput;
export type CapabilityFormData = Omit<
  CapabilityInput,
  'teamPublicId' | 'productPublicId' | 'owner' | 'upstreamDependencies'
> & {
  team: { publicId: string };
  product: { publicId: string };
  owner: { id: string };
  upstreamDependencies: { publicId: string }[];
  importance: FieldOption<number>;
};
export type SloFormData = Omit<SloInput, 'objective' | 'capabilityPublicId' | 'type'> & {
  // for some uninvestigated reason, sometimes the form does not convert objective to number
  objective: number | string;
  capability: { publicId: string };
  type: { value: SloType };
  runbookUrl?: string;
  lowTrafficThreshold?: number;
  suppressLowTrafficAlerts?: boolean;
  isUltraSteepBurnEnabled?: boolean;
  serviceId?: { value: string };
  pollinatorUuids?: Array<string>;
  pollinatorArchetypeUuids?: Array<string>;
} & {
  experienceKey?: { value: string };
  population?: {
    value: {
      cohort: string;
      pageLoadType: string;
      metric: string;
      slo: Partial<API.Entity> & { performanceTarget: number };
    };
  };
  performanceTarget: number;
} & {
  serviceId?: string;
  namespace?: string;
  environment?: string;
  rollupType?: string;
  resourceName?: string;
  successExpression?: string;
  failureExpression?: string;
};

// To accommodate the form field not converting values to number, we ensure it here.
const castStringToNumber = (value: number | string): number =>
  typeof value === 'string' ? Number(value) : value;

export const ui2Api: {
  team: (p: TeamFormData) => TeamInput;
  product: (p: ProductFormData) => ProductInput;
  capability: (p: CapabilityFormData) => CapabilityInput;
  slo: (p: SloFormData) => SloInput;
  slogroup: (p: any) => SloGroupInput;
} = {
  team: ({ opsgenieTeamId: _, org, ...rest }) => {
    const orgData =
      org && org.orgId
        ? {
            atlassianOrgId: org.orgId,
            atlassianOrgStatus: TeamInputAtlassianOrgStatusEnum.Verified,
          }
        : {};
    return {
      opsgenieTeamId: undefined,
      ...rest,
      ...orgData,
    };
  },
  slogroup: identity,
  product: identity,
  capability: ({
    description = '',
    name,
    team,
    product,
    owner,
    importance,
    upstreamDependencies,
  }) => ({
    name,
    description,
    owner: owner.id,
    importance: importance.value,
    // NOTE: the client does dependencies as a set and this fails to be
    // stringified, so we convert to a string[] which the client serializes
    // correctly.
    upstreamDependencies: (upstreamDependencies.map(({ publicId }) => publicId) as unknown) as Set<
      string
    >,
    productPublicId: product.publicId,
    teamPublicId: team.publicId,
  }),
  slo: ({
    description = '',
    runbookUrl = '',
    lowTrafficThreshold,
    suppressLowTrafficAlerts,
    isUltraSteepBurnEnabled,
    name,
    objective,
    type,
    goodQuery,
    badQuery,
    capability,
    isDraft,
    experienceKey = { value: '' },
    population = {
      value: { cohort: '', pageLoadType: '', metric: '' },
    },
    performanceTarget,
    manualTag,
    serviceId,
    namespace,
    environment,
    rollupType,
    resourceName,
    successExpression,
    failureExpression,
    pollinatorUuids,
    pollinatorArchetypeUuids,
  }) => {
    const toStringArray = (arr?: any[]): string[] | undefined =>
      arr
        ?.map(v => {
          if (typeof v === 'string') return v;
          // handle option objects like { value: string }
          if (v && typeof v === 'object' && typeof (v as any).value === 'string') {
            return (v as any).value;
          }
          return undefined;
        })
        .filter((v): v is string => !!v);

    const sfxQueryIndicatorFields = {
      goodQuery: goodQuery || undefined,
      badQuery: badQuery || undefined,
    };

    const uiMetricIndicatorFields = {
      experienceKey: experienceKey.value,
      population: {
        cohort: population.value?.cohort || '',
        pageLoadType: population.value?.pageLoadType || '',
        metric: population.value?.metric || '',
      },
      performanceTarget: castStringToNumber(performanceTarget),
    };

    const cloudWatchIndicatorFields = {
      serviceId: serviceId || undefined,
      namespace: namespace || undefined,
      environment: environment || undefined,
      rollupType: rollupType || undefined,
      resourceName: resourceName || undefined,
      successExpression: successExpression || undefined,
      failureExpression: failureExpression || undefined,
    };

    let indicatorFields;

    switch (type.value) {
      case SloType.Reliability:
      case SloType.Performance:
        indicatorFields = sfxQueryIndicatorFields;
        break;
      case SloType.UiPerformance:
        indicatorFields = uiMetricIndicatorFields;
        break;
      case SloType.ReliabilityCloudwatch:
      case SloType.PerformanceCloudwatch:
        indicatorFields = cloudWatchIndicatorFields;
        break;
      default:
        assertUnreachable(type.value);
    }

    return {
      name,
      isDraft,
      description,
      runbookUrl,
      lowTrafficThreshold: lowTrafficThreshold
        ? castStringToNumber(lowTrafficThreshold)
        : lowTrafficThreshold,
      suppressLowTrafficAlerts,
      isUltraSteepBurnEnabled,
      type: type.value,
      objective: castStringToNumber(objective),
      capabilityPublicId: capability.publicId,
      manualTag,
      pollinatorUuids: toStringArray(pollinatorUuids),
      pollinatorArchetypeUuids: toStringArray(pollinatorArchetypeUuids),
      serviceId: serviceId ? serviceId.value : undefined,
      ...indicatorFields,
    };
  },
};

export const api2Ui: Record<Api2UiMappingType, Function> = {
  slogroup: identity,
  org: (org: OrgDTO) => ({
    ...org,
    label: org.orgName,
    value: org.orgId,
  }),
  pollinator: (pollinator: PollinatorDTO) => ({
    ...pollinator,
    label: pollinator.checkName,
    value: pollinator.uuid,
  }),
  pollinatorArchetype: (pollinatorArchetype: PollinatorArchetypeDTO) => ({
    ...pollinatorArchetype,
    label: pollinatorArchetype.checkName,
    value: pollinatorArchetype.uuid,
  }),
  team: identity,
  product: identity,
  capability: ({ team, product, importance, upstreamDependencies, ...other }: CapabilityDTO) => ({
    team: {
      ...team,
      label: team.name,
      value: team.publicId,
    },
    product: {
      ...product,
      label: product.name,
      value: product.publicId,
    },
    importance,
    upstreamDependencies: upstreamDependencies?.map(upstreamDependency => ({
      ...upstreamDependency,
      label: upstreamDependency.name,
      value: upstreamDependency.publicId,
    })),
    ...other,
  }),
  slo: ({
    publicId,
    description,
    runbookUrl,
    lowTrafficThreshold,
    suppressLowTrafficAlerts,
    isUltraSteepBurnEnabled,
    serviceId,
    name,
    objective,
    indicator,
    type,
    parent,
    isDraft,
    manualTag,
    pollinatorUuids,
    pollinatorArchetypeUuids,
    sloInstance,
  }: SloDTO & {
    uiMetrics?: API.UIMetrics.Config;
  }): SLOCrudPayload => {
    const defaultFields = {
      name,
      description,
      runbookUrl,
      lowTrafficThreshold: lowTrafficThreshold === null ? undefined : lowTrafficThreshold,
      suppressLowTrafficAlerts,
      isUltraSteepBurnEnabled,
      type:
        Object.values(SloTypeFields).find(field => field.value === type) ||
        SloTypeFields.RELIABILITY,
      objective,
      isDraft,
      capability: {
        label: parent.name,
        value: parent.publicId,
        publicId: parent.publicId,
      },
      serviceId: serviceId
        ? {
            label: serviceId,
            value: serviceId,
          }
        : undefined,
      manualTag,
      pollinatorUuids,
      pollinatorArchetypeUuids,
      sloInstance,
    };

    const signalFlowConfigurableFields = {
      goodQuery: indicator?.goodQuery ?? '',
      badQuery: indicator?.badQuery ?? '',
    };

    const cloudWatchConfigurableFields = {
      serviceIdCW: indicator?.serviceId ?? '',
      namespace: indicator?.namespace ?? '',
      environment: indicator?.environment ?? '',
      rollupType: indicator?.rollupType ?? '',
      resourceName: indicator?.resourceName ?? '',
      successExpression: indicator?.successExpression ?? '',
      failureExpression: indicator?.failureExpression ?? '',
    };

    const uiMetricsFields = {
      uiMetrics: {
        experience: indicator?.uiMetrics?.experience,
        population: {
          ...indicator?.uiMetrics?.population,
          /* Map the current SLO to the population -> */
          slo: {
            publicId,
            name,
          },
        },
      },
    };

    return {
      ...defaultFields,
      ...signalFlowConfigurableFields,
      ...uiMetricsFields,
      ...cloudWatchConfigurableFields,
    };
  },
};
